// /badges: the member's collection. The badges within reach (Next up), shelves by
// family with every held badge, the next tier of each category as a ghost with its
// progress, and the detail sheet.
// /badges?badge=<code> opens that badge's sheet (the reveal tiles link here).
import React, { useCallback, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useGetBadgeCatalogQuery, useGetMyBadgesQuery } from "../../../services/rtkApi/badgesApi";
import { useAppSelector } from "../../../store/hooks";
import NextBadges from "../goals/NextBadges";
import type { CategoryProgress, HeldBadge } from "../types";
import BadgeDetailSheet, { type DetailBadge } from "./BadgeDetailSheet";
import BadgeShelves from "./BadgeShelves";
import { BadgeTile } from "./BadgeTile";
import { MAX_PINS } from "./badgeFormat";
import "./badgeCollection.css";

export interface ResolvedDetail {
  badge: DetailBadge;
  held: boolean;
  progress: CategoryProgress | null;
}

/** Find what ?badge=<code> points at: a held badge, a progress ghost, or a catalogue entry. */
export function resolveDetail(
  code: string | null,
  held: HeldBadge[] | undefined,
  progress: CategoryProgress[] | undefined,
  catalog: DetailBadge[] | undefined
): ResolvedDetail | null {
  if (!code) return null;
  const mine = held?.find((b) => b.code === code);
  if (mine) return { badge: mine, held: true, progress: null };
  const ghost = progress?.find((p) => p.next?.code === code);
  if (ghost?.next) return { badge: ghost.next, held: false, progress: ghost };
  const def = catalog?.find((b) => b.code === code);
  if (def) return { badge: def, held: false, progress: progress?.find((p) => p.category === def.category) ?? null };
  return null;
}

const BadgeCollectionPage: React.FC = () => {
  const auth = useAppSelector((s) => s.auth);
  const booting = auth.bootstrapStatus !== "ready";
  const ready = !booting && !!auth.token;
  const { data, isLoading, isError, refetch } = useGetMyBadgesQuery(undefined, { skip: !ready });
  const [params, setParams] = useSearchParams();
  const openCode = params.get("badge");

  const heldOrGhost = useMemo(() => resolveDetail(openCode, data?.badges, data?.progress, undefined), [openCode, data]);
  const { data: catalog } = useGetBadgeCatalogQuery(undefined, { skip: !openCode || !data || !!heldOrGhost });
  const detail = heldOrGhost ?? resolveDetail(openCode, data?.badges, data?.progress, catalog?.badges);

  useEffect(() => {
    document.title = "NΛSOS Your badges";
  }, []);

  const openBadge = useCallback(
    (code: string) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("badge", code);
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );
  const closeBadge = useCallback(
    (open: boolean) => {
      if (open) return;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("badge");
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const pinned = data?.pinned ?? [];
  const badges = data?.badges ?? [];
  const pinnedBadges = pinned.map((code) => badges.find((b) => b.code === code)).filter((b): b is HeldBadge => !!b);
  const copies = badges.reduce((n, b) => n + (b.count || 1), 0);

  let body: React.ReactNode;
  if (booting) {
    body = <p className="bcol-page__status">Loading</p>;
  } else if (!auth.token) {
    body = (
      <div className="bcol-empty">
        <p>Your badges live here. Log in to see them.</p>
        <Link className="bcol-btn bcol-btn--pin" to={`/login?return=${encodeURIComponent("/badges")}`}>
          Log in
        </Link>
      </div>
    );
  } else if (isLoading) {
    body = <p className="bcol-page__status">Loading your badges</p>;
  } else if (isError || !data) {
    body = (
      <div className="bcol-empty">
        <p>Could not load your badges right now.</p>
        <button type="button" className="bcol-btn" onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );
  } else {
    body = (
      <>
        <p className="bcol-page__stats">
          {badges.length} {badges.length === 1 ? "badge" : "badges"}
          {copies > badges.length ? `, ${copies} copies` : ""} · {pinned.length} of {MAX_PINS} pinned
        </p>

        <NextBadges onOpen={openBadge} />

        <section className="bcol-pinned" aria-labelledby="bcol-pinned-title">
          <h2 id="bcol-pinned-title" className="bcol-pinned__title">
            On your profile
          </h2>
          {pinnedBadges.length ? (
            <div className="bcol-pinned__row">
              {pinnedBadges.map((b) => (
                <BadgeTile key={b.code} badge={b} size="card" pinned onOpen={openBadge} />
              ))}
            </div>
          ) : (
            <p className="bcol-pinned__hint">
              Pin up to {MAX_PINS} badges to show them on your profile. Until you do, it shows your rarest three.
            </p>
          )}
        </section>

        {badges.length === 0 && (
          <div className="bcol-empty bcol-empty--inline">
            <p>No badges yet. They come from taking part: make an AOTD, rate a coffee, join the morning race.</p>
          </div>
        )}

        <BadgeShelves badges={badges} progress={data.progress} pinned={pinned} onOpen={openBadge} />
      </>
    );
  }

  return (
    <div className="bcol-page">
      <div className="bcol-page__inner">
        <header className="bcol-page__head">
          <h1 className="bcol-page__title">Your badges</h1>
        </header>
        {body}
      </div>
      <BadgeDetailSheet
        badge={detail?.badge ?? null}
        held={detail?.held ?? false}
        progress={detail?.progress ?? null}
        mode="own"
        pinned={pinned}
        open={!!openCode && !!detail}
        onOpenChange={closeBadge}
      />
    </div>
  );
};

export default BadgeCollectionPage;
